import { Component, OnInit, AfterViewInit, Input, Output, EventEmitter, OnChanges, ViewChild, ElementRef, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { AsyncSubject, Subject } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { environment } from '../../../../../../environments/environment';
import { FormField } from '../../../../models/form-builder/form-field.model';
import { GridFieldType } from '../../../../models/form-builder/form-field-types';
import { Form, ISetOfStrings, IFieldNameValues } from '../../../../models/form-builder/form.model';
import { FormFieldPropertyEnum } from '../../../../models/form-builder/form-field-property-enum.model';
import { SelectOptionsDialog } from '../../../../dialogs/select-options/select-options.dialog';
import { HtmlEditorDialog } from '../../../../dialogs/html-editor/html-editor.dialog';
import { ExportMetadataService } from '../../../../services/export-metadata.service';
import { AlertDialogComponent, AlertDialogModel } from '../../../../dialogs/alert/alert-dialog.component';
import { ConstraintFromMdsDialog, ConstraintFromMdsDialogModel } from '../../../../dialogs/constraint-from-mds-dialog/constraint-from-mds-dialog.component';
import { GridPresetFiltersDialog, GridPresetFiltersDialogModel } from '../../../../dialogs/grid-preset-filters/grid-preset-filters-dialog.component'
import { CascadingDropdownConfigRow } from '../../../../models/site-metadata/cascading-drop-down-config-row.model';
import { FormFieldSelectOptionsConstraint } from '../../../../models/form-builder/field-constraints/field-select-options-constraint.model';
import { FormFieldRegularExpressionConstraint } from '../../../../models/form-builder/field-constraints/field-regular-expression-constraint.model';
import { FormFieldCascadingDropdownConstraint } from '../../../../models/form-builder/field-constraints/field-cascading-dropdown-constraint.model';
import { FormFieldDateRangeConstraint } from '../../../../models/form-builder/field-constraints/field-date-range-constraint.model';
import { FormFieldNumericRangeConstraint } from '../../../../models/form-builder/field-constraints/field-numeric-range-constraint.model';
import { NoValueFormFieldConstraint } from '../../../../models/form-builder/field-constraints/no-value-form-field-constraint.model';
import { SiteSelectOptions } from './constraints/site-select-options';
import { SiteRegularExpressions } from './constraints/site-regular-expressions';
import { SiteCascadingDropdowns } from './constraints/site-cascading-dropdowns';
import { SiteDateRanges } from './constraints/site-date-ranges';
import { SiteNumericRanges } from './constraints/site-numeric-ranges';
import { CachedFormFieldService } from '../../../../services/form-field-with-cache.service';
import { IFormFieldConstraint } from '../../../../interfaces/iform-field-constraint.interface';
import { CsvOptionsFileLoader } from '../../../../models/csv-data/csv-options-file-loader.model';
import { CascadingDropDownFormFieldConfig } from '../../../../models/cascading-dropdown/cascading-dropdown-config.model';
import { ISiteFormFieldConstraints } from './constraints/isite-form-field-constraints.interface';
import { SiteFormFieldConstraints } from './constraints/site-form-field-constraints';
import { FixedFirstColumnValues } from '../../../../models/grid/fixed-first-column-values.model';
import { ContactsProperties } from '../../../../models/contacts/contacts-properties.model';
import { CurrentSiteService } from '../../../../services/current-site.service';
import { RoleService } from '../../../../services/role.service';
import { StringUtil } from '../../../../utility-classes/string.util';
import { ExportCsvDialog, ExportCsvDialogOptions } from '../../../../dialogs/export-csv/export-csv.dialog';
import { IDrawerComponentLiaison } from '../../../../interfaces/idrawer-component-liaison';
import { ScriptableBaseComponent } from '../../../../components/scriptable-base/scriptable-base.component';
import { INameToPrettyNameMap } from '../../../../models/component-scripting/additional-element-info.model';
import { FormBuilderService } from '../../../../services/form-builder.service';
import { DataCollectionService } from '../../../../services/data-collection.service';
import { FieldDefinitionService } from '../../../../services/field-definition.service';
import { IFieldDefinitionLogic } from '../../../../interfaces/ifield-definition-logic.interface';
import { CellClickEvent, CellCloseEvent, SelectableSettings } from '@progress/kendo-angular-grid';
import { Keys } from '@progress/kendo-angular-common';
import { ConfigureHomePageGridColumnsDialog } from '../../../../dialogs/configure-home-page-grid-columns/configure-home-page-grid-columns.dialog';
import { FormTemplatePropertyInfo } from '../../../../models/form-template-instances/form-template-property-info';
import { FormTemplateProperties } from '../../../../models/form-template-instances/form-template-properties';
import { ExcelTemplateService } from '../../../../services/excel-template.service';
import { MonitorExcelImportToGridHelper } from '../../../../dialogs/async-job-base/monitor-excel-import-to-gride-helper';
import { AsyncJobService } from '../../../../services/async-job.service';
import { AsyncJob } from '../../../../models/async-job.model';
import { ExcelImportExportTemplate } from '../../../../models/site-metadata/excel-import-export-template.model';
import { UploadIconDialog } from '../../../../dialogs/upload-icon/upload-icon.dialog';
import { ListIconsDialog } from '../../../../dialogs/list-icons/list-icons.dialog';
import { FieldDefinition } from '../../../../models/form-builder/field-definition.model';
import { FormTemplateInstancesButtonInfo } from '../../../../models/form-template-instances/form-template-instances-button-info';
import { FormTemplateInstancesButtons } from '../../../../models/form-template-instances/form-template-instances-buttons';
import { FormTemplateInstancesConfig } from '../../../../models/form-template-instances/form-template-instances-config.model';
import { KendoGridOperatorName } from '../../../../enums/kendo-grid/kendo-grid-operator-name.enum';
import { MetadataFieldFactory } from '../../../../models/form-template-instances/metadata-field-factory.model'
import { CurrentUser } from '../../../../../security/current-user.model';
import { CurrentUserService } from '../../../../../security/current-user.service';
import { FormFieldCascadingDropdownConstraintLiaison } from './constraints/field-cascading-dropdown-constraint-liaison';
// Define internally used structures.

class RoleNamesProperties {
    public siteRoleNames: string[] = [];
    public selectedRoleNames: string[] = [];

    public constructor(showToHideFromRoleNames: string) {
        if ((showToHideFromRoleNames != null) && (showToHideFromRoleNames.trim() != ''))
            this.selectedRoleNames = showToHideFromRoleNames.split(',');
    }
}

// Define an internally used class related to grid behavior.
class GridColumnBehavior {
    behaviorValue: string;
    behaviorTitle: string;
}


// Define this component.
@Component({
    selector: 'app-form-field-properties',
    templateUrl: './form-field-properties.component.html',
    styleUrls: ['./form-field-properties.component.scss', '../../../properties.scss'],
    encapsulation: ViewEncapsulation.None //enable CSS overrides
    ,
    standalone: false
})
export class FormFieldPropertiesComponent extends ScriptableBaseComponent implements OnInit, AfterViewInit, OnChanges {
    //Properties.
    @Input()
    field: FormField;
    @Input()
    form: Form; // Used to check for form field name unique values.

    @Input()
    properties: string[];

    @Input()
    constraintNames: string[];
    @Input()
    customRegexConstraintName: string;

    @Input()
    displayFormatValues: string[];

    @Input()
    displayRowsValues: string[];

    @Input()
    basicPropertiesExpanded: boolean = true; // Otherwise the advanced options will be initially expanded.

    @Input() drawerComponentLiaison: IDrawerComponentLiaison = null; // Added 11-06-2022.

    @Output()
    onClose: EventEmitter<boolean> = new EventEmitter();

    @Output()
    propertyChanged: EventEmitter<Object> = new EventEmitter<Object>();

    @Output()
    configureFlex: EventEmitter<any> = new EventEmitter();

    private originalField: FormField;

    @ViewChild('displayName') displayNameInput: ElementRef<HTMLInputElement> = null;
    @ViewChild('selectOptionsFile') selectOptionsFile; // Used to load select options.
    @ViewChild('selectTemplateFile') selectTemplateFile;
    @ViewChild('fixedFirstColumnFile') fixedFirstColumnFile: ElementRef<HTMLInputElement> = null; // used to set fixed row headings on a grid

    private minDateValue: UntypedFormControl = null;
    private maxDateValue: UntypedFormControl = null;

    private originalFieldName: string = null;
    private fieldNameError: string = null;

    private constraintProperties: SiteFormFieldConstraints = new SiteFormFieldConstraints();
    @ViewChild('selectOptionsConstraintName') selectOptionsConstraintNameInput: ElementRef<HTMLInputElement>;
    @ViewChild('regularExpressionConstraintName') regularExpressionConstraintNameInput: ElementRef<HTMLInputElement>;
    @ViewChild('cascadingDropdownConstraintName') cascadingDropdownConstraintNameInput: ElementRef<HTMLInputElement>;
    @ViewChild('numericRangeConstraintName') numericRangeConstraintNameInput: ElementRef<HTMLInputElement>;
    @ViewChild('dateRangeConstraintName') dateRangeConstraintNameInput: ElementRef<HTMLInputElement>;

    private optionsLoadedFromCsvFileFlag: boolean = false;
    private optionsLoadedFromMDSFlag: boolean = false;
    private contactsProperties: ContactsProperties = null;

    // Properties related to role names.
    private roleNamesProperties: RoleNamesProperties = null;

    // properties related to Configuring Form Template Instance Grids on Home Pages
    private formTemplateProperties: FormTemplateProperties = new FormTemplateProperties();
    public formTemplatePropertiesGridSelectionSettings: SelectableSettings;
    private selectedGridFieldId: number;
    private commandColumnPostion: string;

    // Grid column behavior settings.
    private readonly gridColumnBehaviors: GridColumnBehavior[] =
        [
            { behaviorValue: 'ShowInRowDataOnly', behaviorTitle: 'Show in Row' },
            { behaviorValue: 'ShowInExpandedDetailOnly', behaviorTitle: 'Show In Detail' }
        ];

    private formTemplateInstancesButtons: FormTemplateInstancesButtons = new FormTemplateInstancesButtons();
    //***An array is required when using multi-select. Will populate selectedFormTemplateIds with this value onSave*/
    private arrayOfSelectedFormTemplateIds: number[] = null;

    // Constructor.
    public constructor(private dialog: MatDialog,
        private exportMetadataService: ExportMetadataService,
        private formFieldService: CachedFormFieldService,
        private currentSiteService: CurrentSiteService,
        private currentUserService: CurrentUserService,
        private roleService: RoleService,
        private formBuilderService: FormBuilderService,
        private dataCollectionService: DataCollectionService,
        private fieldDefinitionService: FieldDefinitionService,
        private excelTemplateService: ExcelTemplateService,
        private asyncJobService: AsyncJobService,
        private formBuilder: UntypedFormBuilder,
        elForBaseClass: ElementRef) {
        super(elForBaseClass);
        let mapOfInputNamesToPrettyNames: INameToPrettyNameMap =
        {
            'field.displayName': 'Display Name',
            'field.name': 'Internal Name',
            'field.defaultValue': 'Default Value',
            'field.placeholderText': 'Placeholder Text',
            'field.tooltip': 'ToolTip',
            'field.regex': 'Regular Expression',
            'field.regularExpressionConstraintMessage': 'Regular Expression Message',
            'field.regularExpressionConstraintName': 'Regular Expression Constraint Name'
        };
        this.element('#formFieldPropertiesForm').hasInputs().withNameToPrettyNameMap(mapOfInputNamesToPrettyNames);
        this.element('#buttonWrapperDiv').hasButtons();
    }

    // Life cycle methods.
    public ngOnInit(): void {
        super.ngOnInit(); // Important to call this as the first line in this method.

        // Manually initialize my drawer liaison (since we are not inheriting from DrawerComponentBase).
        if (this.drawerComponentLiaison != null)
            this.drawerComponentLiaison.drawerComponentInitialized(this);

        // Initialize some values.
        this.field.transientToBeAppliedInternalName = this.field.name.toLowerCase();
        this.originalFieldName = this.field.name;

        //***Initialize multi-selected templateIds for simple Form Grid */
        if (this.field?.selectedFormTemplateIds != null) {
            this.arrayOfSelectedFormTemplateIds = (this.field.selectedFormTemplateIds.split(',')).map(x => { return Number(x) });
        }

        //***Initialized the original values 
        this.originalField = Object.assign({}, this.field);

        // Handle property-specific setup.
        this.minDateValue = new UntypedFormControl();
        if ((this.field.minDate != null) && (this.field.minDate.toString().trim() !== '')) {
            let hshDateInfo: any = FormField.DateToUtcDate(this.field.minDate);
            let strUTCDate: string = hshDateInfo['utcDate'];
            let minDate: Date = hshDateInfo['dateValue'];

            this.minDateValue.setValue(strUTCDate);
        }

        this.maxDateValue = new UntypedFormControl();
        if ((this.field.maxDate != null) && (this.field.maxDate.toString().trim() !== '')) {
            let hshDateInfo: any = FormField.DateToUtcDate(this.field.maxDate);
            let strUTCDate: string = hshDateInfo['utcDate'];
            let maxDate: Date = hshDateInfo['dateValue'];

            this.maxDateValue.setValue(strUTCDate);
        }

        // Handle configuration data related to the following three properties:
        //
        //     1. 'selectOptions' -- e.g. for dropdown fields;
        //     2. 'regex' -- presently for short text fields only; and
        //     3. 'selectOptionsFile' -- presently for cascading dropdown fields only.
        if (this.hasProperty('selectOptions') || this.hasProperty('selectOptionsProperty')) {
            this.constraintProperties.selectOptionsProperties = new SiteSelectOptions(this.form, this.formFieldService);
            if (this.hasProperty('selectOptionsProperty'))
                this.constraintProperties.selectOptionsProperties.enableEditing();

            this.formFieldService.getSiteNamedSelectOptions(this.form.dataCollectionId).then(response => {
                this.constraintProperties.selectOptionsProperties.constraintsLoaded(this.field, response);
            });
        }

        if (this.hasProperty('regex') || this.hasProperty('regexProperty')) {
            this.constraintProperties.regularExpressionProperties = new SiteRegularExpressions(this.form, this.formFieldService);
            if (this.hasProperty('regexProperty'))
                this.constraintProperties.regularExpressionProperties.enableEditing();

            this.formFieldService.getSiteNamedRegularExpressions(this.form.dataCollectionId).then(response => {
                this.constraintProperties.regularExpressionProperties.constraintsLoaded(this.field, response);
            });
        }

        if (this.hasProperty('selectOptionsFile') || this.hasProperty('selectOptionsFileProperty')) {
            this.constraintProperties.cascadingDropdownProperties = new SiteCascadingDropdowns(this.form, this.formFieldService);
            if (this.hasProperty('selectOptionsFileProperty'))
                this.constraintProperties.cascadingDropdownProperties.enableEditing();

            this.formFieldService.getSiteNamedCascadingDropdowns(this.form.dataCollectionId).then(response => {
                this.constraintProperties.cascadingDropdownProperties.constraintsLoaded(this.field, response);
            });
        }

        if (this.hasProperty('minDate') || this.hasProperty('maxDate') || this.hasProperty('minDateProperty') || this.hasProperty('maxDateProperty')) {
            this.constraintProperties.dateRangeProperties = new SiteDateRanges(this.form, this.formFieldService);
            if (this.hasProperty('minDateProperty') || this.hasProperty('maxDateProperty'))
                this.constraintProperties.dateRangeProperties.enableEditing();

            this.formFieldService.getSiteNamedDateRanges(this.form.dataCollectionId).then(response => {
                this.constraintProperties.dateRangeProperties.constraintsLoaded(this.field, response);
            });
        }

        if (this.hasProperty('minValue') || this.hasProperty('maxValue') || this.hasProperty('minValueProperty') || this.hasProperty('maxValueProperty')) {
            this.constraintProperties.numericRangeProperties = new SiteNumericRanges(this.form, this.formFieldService);
            if (this.hasProperty('minValueProperty') || this.hasProperty('maxValueProperty'))
                this.constraintProperties.numericRangeProperties.enableEditing();

            this.formFieldService.getSiteNamedNumericRanges(this.form.dataCollectionId).then(response => {
                this.constraintProperties.numericRangeProperties.constraintsLoaded(this.field, response);
            });
        }

        // Set up contacts-specific properties if applicable.
        if (this.hasProperty(FormFieldPropertyEnum.SHOW_CONTACTS_PROPERTIES)) {
            this.contactsProperties = new ContactsProperties(this.field);
        }

        // Fetch role names used in the current site, if applicable.
        if (this.hasProperty(FormFieldPropertyEnum.ROLE_NAMES)) {
            this.roleNamesProperties = new RoleNamesProperties(this.field.showToHideFromRoleNames);

            let dataCollectionId: number = this.currentSiteService.Site.id;
            this.roleService.getSiteRoles(dataCollectionId).then(roles => {
                this.roleNamesProperties.siteRoleNames = roles.map(r => r.name);
            });
        }

        // Configuration for form fields with decimal values.
        if (this.hasProperty(FormFieldPropertyEnum.NUM_DIGITS_AFTER_DECIMAL_POINT) && (this.field.numDigitsAfterDecimalPoint == 0))
            this.field.numDigitsAfterDecimalPoint = 2; // A default.

        if (this.hasProperty(FormFieldPropertyEnum.ROUND_TO_NUM_DIGITS_AFTER_DECIMAL_POINT) && (this.field.roundToNumDigitsAfterDecimalPoint == 0))
            this.field.roundToNumDigitsAfterDecimalPoint = 2;

        // If we have a formula, output a property updated at the outset.
        if (this.hasProperty(FormFieldPropertyEnum.FORMULA)) {
            let hshEventData = {
                propertyName: FormFieldPropertyEnum.FORMULA,
                formField: this.field
            }
            this.propertyChanged.emit(hshEventData);
        }

        //VNEXT-1066: KLW - Code for Banner implementation
        if (this.hasProperty(FormFieldPropertyEnum.DISPLAY_KENDO_GRID_ROWS)) {
            //VNEXT-1066: KLW - Set a default value to display
            if (this.field.displayKendoGridRows < 1) {
                this.field.displayKendoGridRows = 5;
            }
        }

        // Do we need to load form template names?
        if (this.hasProperty('formTemplate')) {
            this.dataCollectionService.getFormsBySite(this.currentSiteService.Site.id, 'names').then(response => {
                if ((response != null) && (response.length > 0)) {
                    this.formTemplateProperties.siteFormTemplates = [];
                    for (let index: number = 0; index < response.length; index++) {
                        let form: Form = response[index];
                        this.formTemplateProperties.siteFormTemplates.push(form);
                    }
                }
            });
        }
        if (this.hasProperty('formTemplateProperties')) {
            this.fetchFormTemplateIfSelected();
            this.formTemplatePropertiesGridSelectionSettings = {
                checkboxOnly: true,
                mode: 'multiple'
            }
        }
        //debugger;
        // Form template instances buttons:
        this.formTemplateInstancesButtons.createButtonInfoFromSavedConfig(this.field.formTemplateInstanceActionButtonsConfig);
    }

    public ngAfterViewInit(): void {
        // Set focus to the primary input field.
        if ((this.displayNameInput != null) && (this.displayNameInput.nativeElement != null)) {
            setTimeout(() => {
                this.displayNameInput.nativeElement.focus();
            }, 800);
        }

        // Place constraint name input fields into a map.
        this.constraintProperties.mapOfConstraintNameInputFields['selectOptions'] = this.selectOptionsConstraintNameInput;
        this.constraintProperties.mapOfConstraintNameInputFields['regex'] = this.regularExpressionConstraintNameInput;
        this.constraintProperties.mapOfConstraintNameInputFields['selectOptionsFile'] = this.cascadingDropdownConstraintNameInput;
        this.constraintProperties.mapOfConstraintNameInputFields['minValue'] = this.numericRangeConstraintNameInput;
        this.constraintProperties.mapOfConstraintNameInputFields['minDate'] = this.dateRangeConstraintNameInput;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.originalField = Object.assign({}, this.field);

        if (this.displayFormatValues && (this.displayFormatValues.length > 0) && (!this.field.displayFormat)) {
            this.field.displayFormat = this.displayFormatValues[0];
        } else if (this.displayRowsValues && (this.displayRowsValues.length > 0) && (!this.field.displayRows)) {
            this.field.displayRows = this.displayRowsValues[0];
        }
    }

    // Methods called by my HTML code.
    public isInGrid(): boolean {
        return this.field.parentFormFieldId > 0;
    }

    public hasProperty(fieldName: string): boolean {
        let hasProperty: boolean = false;

        if (this.properties)
            hasProperty = this.properties.includes(fieldName);

        return hasProperty;
    }

    public ConfigureFlexSelect() {
        this.configureFlex.emit();
    }

    public getInstructionsEditorCssDefaults(): string {
        return `
        .k-content {
            font-family:Arial;
        }`
    };

    public get hasAnyAdvancedProperty(): boolean {
        return (
            this.hasProperty('name') ||
            this.hasProperty('defaultValue') ||
            this.hasProperty('placeholderText') ||
            this.hasProperty('toolTip') ||
            this.hasProperty('displayRows') ||
            this.hasProperty('showContactsProperties') ||
            this.hasProperty('fixedFirstColumnFile') ||
            this.hasProperty('selectOptionsFile') ||
            this.hasProperty('selectOptionsFileProperty') ||
            this.hasProperty('showCascadingDropdownIndicesInPreviewMode') ||
            this.hasProperty('formula') ||
            this.hasProperty('constraint') ||
            this.hasProperty('regex') ||
            this.hasProperty('regexProperty') ||
            this.hasProperty('readOnly') ||
            this.hasProperty('showPreview') ||
            this.hasProperty('footer') ||
            this.hasProperty('numDisplayRows') ||
            this.hasProperty('footnote') ||
            this.hasProperty('autocomplete'));
    }

    public hasFormTemplate(): boolean {
        return (this.field.formTemplateId != null) && (this.field.formTemplateId > 0);
    }

    public get ArrayOfSelectedFormTemplateIds(): number[] {
        return this.arrayOfSelectedFormTemplateIds;
    }
    public set ArrayOfSelectedFormTemplateIds(value: number[]) {
        this.arrayOfSelectedFormTemplateIds = value;
        this.field.selectedFormTemplateIds = this.arrayOfSelectedFormTemplateIds.join(',');
    }

    public get BasicPropertiesExpanded(): boolean {
        return this.basicPropertiesExpanded;
    }
    public get AdvancedPropertiesExpanded(): boolean {
        return !this.basicPropertiesExpanded;
    }

    public get SelectOptionsProperties(): SiteSelectOptions {
        return this.constraintProperties.selectOptionsProperties;
    }
    public get RegularExpressionProperties(): SiteRegularExpressions {
        return this.constraintProperties.regularExpressionProperties;
    }
    public get CascadingDropdownProperties(): SiteCascadingDropdowns {
        return this.constraintProperties.cascadingDropdownProperties;
    }
    public get DateRangeProperties(): SiteDateRanges {
        return this.constraintProperties.dateRangeProperties;
    }
    public get NumericRangeProperties(): SiteNumericRanges {
        return this.constraintProperties.numericRangeProperties;
    }

    public get FieldNameError(): string {
        return (this.fieldNameError);
    }
    public get DisableSave(): boolean {
        let disable: boolean = (this.fieldNameError != null);
        if (!disable) {
            // Test other conditions that need to disable the 'Apply' button.
            if (this.hasProperty('selectOptions') || this.hasProperty('selectOptionsProperty')) {
                if (this.SelectOptionsConstraintError != null)
                    disable = true;
            }

            if (this.hasProperty('regex') || this.hasProperty('regexProperty')) {
                if (this.RegularExpressionConstraintError != null)
                    disable = true;
            }

            if (this.hasProperty('selectOptionsFile') || this.hasProperty('selectOptionsFileProperty')) {
                if (this.CascadingDropdownConstraintError != null)
                    disable = true;
            }

            if (this.hasProperty('minDate') || this.hasProperty('maxDate') || this.hasProperty('minDateProperty') || this.hasProperty('maxDateProperty')) {
                if (this.DateRangeConstraintError != null)
                    disable = true;
            }

            if (this.hasProperty('minValue') || this.hasProperty('maxValue') || this.hasProperty('minValueProperty') || this.hasProperty('maxValueProperty')) {
                if (this.NumericRangeConstraintError != null)
                    disable = true;
            }

            //VNEXT-519: KLW - Refinements to the Type ahead functionality
            if (this.hasProperty('autocomplete')) {
                if (this.AutocompleteOptionsError != null)
                    disable = true;
            }

            if (this.contactsProperties != null) {
                if (this.contactsProperties.CheckedFieldNames.length == 0)
                    disable = true;
            }
        }

        return disable;
    }

    public get MinDateValue(): UntypedFormControl {
        return this.minDateValue;
    }
    public get MaxDateValue(): UntypedFormControl {
        return this.maxDateValue;
    }

    public get FieldSelectOptionsEmpty(): boolean {
        return ((this.field.selectOptions == null) || (this.field.selectOptions.trim() == ''));
    }
    public get SelectedSelectOptionsConstraintId(): number {
        return this.field.selectOptionsConstraintId;
    }
    public set SelectedSelectOptionsConstraintId(value: number) {
        this.constraintProperties.selectOptionsProperties.setId(this.form, this.field, value);
        this.handleModelChange(FormFieldPropertyEnum.SELECT_OPTIONS, value); // To notify the correspond form field component.
    }
    public get SiteSelectOptionsConstraintsEmpty(): boolean {
        return this.constraintProperties.selectOptionsProperties.constraintsEmpty;
    }
    public get SiteSelectOptionsConstraints(): FormFieldSelectOptionsConstraint[] {
        let siteConstraints: FormFieldSelectOptionsConstraint[] = [];

        if (this.constraintProperties.selectOptionsProperties.constraints != null) {
            siteConstraints = <FormFieldSelectOptionsConstraint[]>this.constraintProperties.selectOptionsProperties.constraints;
        }

        return siteConstraints;
    }
    public getAssignConstraintNameButtonTitle(defaultTitle: string, siteConstraints: ISiteFormFieldConstraints, constraintId: number): string {
        return FormFieldPropertiesComponent.staticGetAssignConstraintNameButtonTitle(defaultTitle, siteConstraints, constraintId);
    }
    public static staticGetAssignConstraintNameButtonTitle(defaultTitle: string, siteConstraints: ISiteFormFieldConstraints, constraintId: number): string {
        let title: string = defaultTitle;

        if ((constraintId != null) && (constraintId > 0)) {
            if (siteConstraints && siteConstraints.editingDisabled)
                title = 'Edit existing constraint';
            else
                title = 'End editing existing constraint';
        }

        return title;

    }
    public get SelectOptionsConstraintSelected(): boolean {
        return ((this.field.selectOptionsConstraintId != null) && (this.field.selectOptionsConstraintId != 0));
    }
    public get SelectOptionsNameDisabled(): boolean {
        return (this.constraintProperties.selectOptionsProperties != null ? this.constraintProperties.selectOptionsProperties.editingDisabled : false);
    }
    public get SelectOptionsPropertyEditDisabled(): boolean {
        let disabled: boolean = false;

        // If we are just editing a constraint's list options/
        // select options property, do not disable editing.
        if (!this.hasProperty('selectOptionsProperty')) {
            // Disable property editing if the name field cannot be edited.
            disabled = (this.SelectOptionsConstraintSelected && this.SelectOptionsNameDisabled);
        }

        return disabled;
    }
    public get SelectOptionsConstraintError(): string {
        let error: string = FormFieldPropertiesComponent.constraintDupNameError(this.SelectedSelectOptionsConstraintId, this.field.selectOptionsConstraintName, this.constraintProperties.selectOptionsProperties, 'list options');

        if ((error == null) && this.constraintProperties.requireConstraintNames) {
            if ((!this.isEmptyString(this.field.selectOptions)) && this.isEmptyString(this.field.selectOptionsConstraintName))
                error = 'A dropdown list constraint name is required';
        }

        if ((error == null) && this.hasProperty('selectOptionsProperty')) {
            // We are defining a new constraint in the form field constraints component (only
            // that component uses property names like 'selectOptionsProperty' or 'xxxProperty').
            if (this.isEmptyString(this.field.selectOptions))
                error = 'A list options property is required';
        }

        return error;
    }
    public get ProposedConstraintName(): string {
        let fieldName: string = this.field.name;
        if ((this.field.displayName != null) && (this.field.displayName.trim() != ''))
            fieldName = this.field.displayName;

        let proposedConstraintName: string = `${fieldName} Constraint`;
        return proposedConstraintName;
    }

    public get FieldRegularExpressionEmpty(): boolean {
        return ((this.field.regex == null) || (this.field.regex.trim() == ''));
    }
    public get SelectedRegularExpressionConstraintId(): number {
        return (this.field.regularExpressionConstraintId != null ? this.field.regularExpressionConstraintId : 0);
    }
    public set SelectedRegularExpressionConstraintId(value: number) {
        if (this.constraintProperties.regularExpressionProperties)
            this.constraintProperties.regularExpressionProperties.setId(this.form, this.field, value);
        if ((value != null) && (value != 0) && (this.customRegexConstraintName != null))
            this.field.constraintName = this.customRegexConstraintName;
        this.handleModelChange(FormFieldPropertyEnum.REGEX, value); // To notify the correspond form field component.
    }
    public get DisableCustomRegexInput(): boolean {
        return ((this.customRegexConstraintName == null) || (this.field.constraintName != this.customRegexConstraintName));
    }
    public get SampleRegularExpressionsUrl(): string {
        return 'https://max.omb.gov/community/x/ICJqJ';
    }
    public get SiteRegularExpressionConstraintsEmpty(): boolean {
        return (this.constraintProperties.regularExpressionProperties != null ? this.constraintProperties.regularExpressionProperties.constraintsEmpty : true);
    }
    public get SiteRegularExpressionConstraints(): FormFieldRegularExpressionConstraint[] {
        return (this.constraintProperties.regularExpressionProperties.constraints != null ? <FormFieldRegularExpressionConstraint[]>this.constraintProperties.regularExpressionProperties.constraints : []);
    }
    public getFormFieldConstraintName(formFieldConstraint: IFormFieldConstraint): string {
        let result: string = formFieldConstraint.name;
        return result;
    }
    public get RegularExpressionConstraintSelected(): boolean {
        let result = ((this.field.regularExpressionConstraintId != null) && (this.field.regularExpressionConstraintId != 0));
        return result;
    }
    public get RegularExpressionNameDisabled(): boolean {
        if (!this.constraintProperties.regularExpressionProperties)
            return false;
        let result = this.constraintProperties.regularExpressionProperties.editingDisabled;
        return result;
    }
    public get RegularExpressionConstraintError(): string {
        let error: string = FormFieldPropertiesComponent.constraintDupNameError(this.SelectedRegularExpressionConstraintId, this.field.regularExpressionConstraintName, this.constraintProperties.regularExpressionProperties, 'regular expression');

        if ((error == null) && this.constraintProperties.requireConstraintNames) {
            if ((!this.isEmptyString(this.field.regex)) && this.isEmptyString(this.field.regularExpressionConstraintName))
                error = 'A regular expression constraint name is required';
        }

        if ((error == null) && this.hasProperty('regexProperty')) {
            // We are defining a new constraint in the form field constraints component (only
            // that component uses property names like 'regexProperty' or 'xxxProperty').
            if (this.isEmptyString(this.field.regex))
                error = 'A regular expression property is required';
        }

        return error;
    }

    //VNEXT-519: KLW - Refinements to the Type ahead functionality
    public get AutocompleteOptionsError(): string {
        var retVal: string = null;

        if (this.field.autocomplete) {
            if (!this.field.autocomplete_Contains && !this.field.autocomplete_StartsWith) {
                retVal = 'You must set either Results Start With or Results Contain';
            }
        }

        return retVal;
    }

    public get SiteCascadingDropdownConstraintsEmpty(): boolean {
        return this.constraintProperties.cascadingDropdownProperties.constraintsEmpty;
    }
    public get SiteCascadingDropdownConstraints(): FormFieldCascadingDropdownConstraint[] {
        return (this.constraintProperties.cascadingDropdownProperties.constraints != null ? <FormFieldCascadingDropdownConstraint[]>this.constraintProperties.cascadingDropdownProperties.constraints : []);
    }
    public get SelectedCascadingDropdownConstraintId(): number {
        return (this.field.cascadingDropdownConstraintId != null ? this.field.cascadingDropdownConstraintId : 0);
    }
    public set SelectedCascadingDropdownConstraintId(value: number) {
        this.constraintProperties.cascadingDropdownProperties.setId(this.form, this.field, value);
        if (value == 0)
            this.clearCascadingDropdownConfigClicked();
        this.handleModelChange(FormFieldPropertyEnum.SELECTED_CASCADING_DROPDOWN_CONSTRAINT);
    }
    public get FieldCascadingDropdownEmpty(): boolean {
        let result = true;
        if (this.constraintProperties.cascadingDropdownProperties) {
            if ((this.field.cascadingDropdownConstraintValue != null) && (this.field.cascadingDropdownConstraintValue.trim() != ''))
                result = false;
            else if (this.field.csvOptionsData != null)
                result = false;
            else if ((this.field.jsonConfig != null) && (this.field.jsonConfig.trim() != ''))
                result = false;
        }
        return result;
    }
    public get CascadingDropdownConstraintSelected(): boolean {
        let result = ((this.field.cascadingDropdownConstraintId != null) && (this.field.cascadingDropdownConstraintId != 0));
        return result;
    }
    public get CascadingDropdownNameDisabled(): boolean {
        let result = (this.constraintProperties.cascadingDropdownProperties != null ? this.constraintProperties.cascadingDropdownProperties.editingDisabled : false);
        return result;
    }
    public get CascadingDropdownConstraintError(): string {
        let error: string = FormFieldPropertiesComponent.constraintDupNameError(this.SelectedCascadingDropdownConstraintId, this.field.cascadingDropdownConstraintName, this.constraintProperties.cascadingDropdownProperties, 'cascading dropdown');

        if ((error == null) && this.constraintProperties.requireConstraintNames) {
            if ((!this.isEmptyString(this.field.jsonConfig)) && this.isEmptyString(this.field.cascadingDropdownConstraintName))
                error = 'A cascading dropdown list constraint name is required';
        }
        // List Value Constraints have this property
        if ((error == null) && this.hasProperty('selectOptionsFileProperty')) {
            // We are defining a new constraint in the form field constraints component (only
            // that component uses property names like 'selectOptionsFileProperty' or 'xxxProperty').
            if (this.isEmptyString(this.field.jsonConfig) && this.isEmptyString(this.field.csvOptionsData?.rawCsv))
                error = 'A cascading dropdown configuration is required';
        }

        return error;
    }
    public get ClearCascadingDropdownConfigDisabled(): boolean {
        return ((this.field.jsonConfig == null) || (this.field.jsonConfig.trim() == ''));
    }

    public get SiteDateRangeConstraintsEmpty(): boolean {
        return this.constraintProperties.dateRangeProperties.constraintsEmpty;
    }
    public get SiteDateRangeConstraints(): FormFieldDateRangeConstraint[] {
        let constraints: FormFieldDateRangeConstraint[] = [];
        if ((this.constraintProperties != null) && (this.constraintProperties.dateRangeProperties != null) && (this.constraintProperties.dateRangeProperties.constraints != null))
            constraints = <FormFieldDateRangeConstraint[]>this.constraintProperties.dateRangeProperties.constraints;
        return constraints;
    }
    public get SelectedDateRangeConstraintId(): number {
        return (this.field.dateRangeConstraintId != null ? this.field.dateRangeConstraintId : 0);
    }
    public set SelectedDateRangeConstraintId(value: number) {
        this.constraintProperties.dateRangeProperties.setId(this.form, this.field, value);
        if (value == 0) {
            this.minDateValue.setValue('')
            this.maxDateValue.setValue('');
        }
        this.handleModelChange(FormFieldPropertyEnum.SELECTED_DATE_RANGE_CONSTRAINT);
    }
    public get DateRangeInputFieldsDisabled(): boolean {
        let disabled: boolean =
            ((this.field.dateRangeConstraintId != null) && (this.field.dateRangeConstraintId != 0) && this.constraintProperties.dateRangeProperties.editingDisabled);
        return disabled;
    }
    public get DateRangeFieldsEmpty(): boolean {
        return ((this.field.minDate == null) && (this.field.maxDate == null));
    }
    public get DateRangeNameDisabled(): boolean {
        let result = (this.constraintProperties.dateRangeProperties != null ? this.constraintProperties.dateRangeProperties.editingDisabled : false);
        return result;
    }
    public get DateRangeConstraintError(): string {
        let errorMessage: string = null;

        if (this.hasProperty('minDate') || this.hasProperty('maxDate') || this.hasProperty('minDateProperty') || this.hasProperty('maxDateProperty')) {
            if ((this.field.dateRangeConstraintName != null) && (this.field.dateRangeConstraintName.trim() != '')) {
                if ((this.field.minDate == null) && (this.field.maxDate == null)) {
                    errorMessage = `Date range constraint '${this.field.dateRangeConstraintName}' must have a value for 'MinDate' and/or 'MaxDate'.`;
                }
            }

            if (errorMessage == null) {
                errorMessage = FormFieldPropertiesComponent.constraintDupNameError(this.SelectedDateRangeConstraintId, this.field.dateRangeConstraintName, this.constraintProperties.dateRangeProperties, 'date range');
            }

            if ((errorMessage == null) && this.constraintProperties.requireConstraintNames) {
                let hasMinDate = ((this.field.minDate != null) && (this.field.minDate.toString().trim() != ''));
                let hasMaxDate = ((this.field.maxDate != null) && (this.field.maxDate.toString().trim() != ''));

                if ((hasMinDate || hasMaxDate) && this.isEmptyString(this.field.dateRangeConstraintName))
                    errorMessage = 'A date range constraint name is required';
            }
        }

        if ((errorMessage == null) && this.hasProperty('minDateProperty')) {
            // We are defining a new constraint in the form field constraints component (only
            // that component uses property names like 'minProperty' or 'xxxProperty').
            if (this.isEmptyString(this.field.minDate) && this.isEmptyString(this.field.maxDate))
                errorMessage = 'A min and/or max date property is required';
        }

        return errorMessage;
    }

    public get SiteNumericRangeConstraintsEmpty(): boolean {
        return this.constraintProperties.numericRangeProperties.constraintsEmpty;
    }
    public get SiteNumericRangeConstraints(): FormFieldNumericRangeConstraint[] {
        return (this.constraintProperties.numericRangeProperties.constraints != null ? <FormFieldNumericRangeConstraint[]>this.constraintProperties.numericRangeProperties.constraints : []);
    }
    public get SelectedNumericRangeConstraintId(): number {
        return (this.field.numericRangeConstraintId != null ? this.field.numericRangeConstraintId : 0);
    }
    public set SelectedNumericRangeConstraintId(value: number) {
        this.constraintProperties.numericRangeProperties.setId(this.form, this.field, value);
        this.handleModelChange(FormFieldPropertyEnum.SELECTED_NUMERIC_RANGE_CONSTRAINT);
    }
    public get NumericRangeInputFieldsDisabled(): boolean {
        let disabled: boolean =
            ((this.field.numericRangeConstraintId != null) && (this.field.numericRangeConstraintId != 0) && this.constraintProperties.numericRangeProperties.editingDisabled);
        return disabled;
    }
    public get NumericRangeFieldsEmpty(): boolean {
        let empty: boolean = true;

        if ((this.field.minValue != null) && (this.field.minValue.toString().trim() != '') && (!isNaN(this.field.minValue)))
            empty = false;
        else if ((this.field.maxValue != null) && (this.field.maxValue.toString().trim() != '') && (!isNaN(this.field.maxValue)))
            empty = false;

        return empty;
    }
    public get NumericRangeNameDisabled(): boolean {
        let result = this.constraintProperties.numericRangeProperties.editingDisabled;
        return result;
    }
    public get NumericRangeConstraintError(): string {
        let errorMessage: string = null;

        if (this.hasProperty('minValue') || this.hasProperty('maxValue') || this.hasProperty('minValueProperty') || this.hasProperty('maxValueProperty')) {
            if (!this.isEmptyString(this.field.numericRangeConstraintName)) {
                if ((this.field.minValue == null) && (this.field.maxValue == null)) {
                    errorMessage = `Numeric range constraint '${this.field.numericRangeConstraintName}' must have a value for 'MinValue' and/or 'MaxValue'.`;
                }
                if (errorMessage == null) {
                    errorMessage = FormFieldPropertiesComponent.constraintDupNameError(this.SelectedNumericRangeConstraintId, this.field.numericRangeConstraintName, this.constraintProperties.numericRangeProperties, 'numeric range');
                }
            } else if (this.constraintProperties.requireConstraintNames) {
                if ((this.field.minValue != null) || (this.field.maxValue != null))
                    errorMessage = 'A numeric range constraint name is required';
            }
        }

        if ((errorMessage == null) && this.hasProperty('minValueProperty')) {
            // We are defining a new constraint in the form field constraints component (only
            // that component uses property names like 'minValueProperty' or 'xxxProperty').
            if ((this.field.minValue == null) && (this.field.maxValue == null))
                errorMessage = 'A min and/or max value property is required';
        }

        return errorMessage;
    }
    public get MinValueError(): string {
        let errorMessage: string = null;

        if (this.hasProperty('minValue')) {
            if ((this.field.minValue != null) && (this.field.minValue.toString().trim() != '') && isNaN(this.field.minValue))
                errorMessage = "Property 'MinValue' is not a valid numeric value.";
        }

        return errorMessage;
    }
    public get MaxValueError(): string {
        let errorMessage: string = null;

        if (this.hasProperty('maxValue')) {
            if ((this.field.maxValue != null) && (this.field.maxValue.toString().trim() != '') && isNaN(this.field.maxValue))
                errorMessage = "Property 'MaxaValue' is not a valid numeric value.";
        }

        return errorMessage;
    }

    public get ContactFieldNames(): string[] {
        return (this.contactsProperties != null ? this.contactsProperties.ContactFieldNames : []);
    }
    public getContactFieldNameFormControl(index: number): UntypedFormControl {
        return (this.contactsProperties != null ? this.contactsProperties.getContactFieldNameFormControl(index) : null);
    }
    public isNotLastContactField(index: number): boolean {
        return (this.contactsProperties != null ? this.contactsProperties.isNotLastContactField(index) : true);
    }
    public get ContactFieldsError(): string {
        let error: string = ((this.contactsProperties != null) && this.contactsProperties.CheckedFieldNames.length == 0 ? 'At least one contact field must be selected' : null);

        return error;
    }

    public get HasNoFixedFirstColumn(): boolean {
        return (this.field.fixedFirstColumnJson == null);
    }

    public get SiteRoleNames(): string[] {
        return (this.roleNamesProperties != null ? this.roleNamesProperties.siteRoleNames : []);
    }
    public get SelectedRoleNames(): string[] {
        return (this.roleNamesProperties != null ? this.roleNamesProperties.selectedRoleNames : []);
    }
    public set SelectedRoleNames(value: string[]) {
        if (this.roleNamesProperties != null) {
            this.roleNamesProperties.selectedRoleNames = value;
            this.field.showToHideFromRoleNames = StringUtil.arrayToDelimitedList(this.roleNamesProperties.selectedRoleNames);
        }
    }
    public get NoRoleNameSelectedError(): string {
        // TO DO:  CODE THIS METHOD.
        return null;
    }

    // ==== Form Templates Stuff for confuguring Home Page Grids ====
    public get FormTemplatePropertiesGridSelectionSettings() {
        return this.formTemplatePropertiesGridSelectionSettings;
    }

    public get SiteFormTemplates(): Form[] {
        let formTemplateList = this.formTemplateProperties.siteFormTemplates != null ? this.formTemplateProperties.siteFormTemplates : [];
        formTemplateList = formTemplateList.sort((x, y) => x.name > y.name ? 1 : 0);
        return formTemplateList;
    }

    // THIS WILL NEED TO BE REMOVED 
    public get FormTemplateButtonInfo(): FormTemplateInstancesButtonInfo[] {
        this.formTemplateInstancesButtons.createDefaultButtonInfoIfNoConfigExists();

        return this.formTemplateInstancesButtons.FormTemplateInstancesButtonInfo;
    }
    public get FormTemplateProperties(): FormTemplatePropertyInfo[] {
        return this.formTemplateProperties.availableProperties;
    }
    public set FormTemplateProperties(vals: any) {
        this.formTemplateProperties.availableProperties = vals;
    }

    public get FormTemplateInstancesGridJsonConfig(): string[] {
        return this.formTemplateProperties.selectedPropertyNames;
    }

    private selectedFormTemplatePropertiesIndexes: number[] = null;
    public get SelectedFormTemplateProperties(): number[] {
        return this.selectedFormTemplatePropertiesIndexes;
    }
    public set SelectedFormTemplateProperties(selectedIndexes: number[]) {
        this.selectedFormTemplatePropertiesIndexes = selectedIndexes;

        for (let i = 0; i < this.formTemplateProperties.availableProperties.length; i++) {
            this.formTemplateProperties.availableProperties[i].isSelected = (selectedIndexes.indexOf(i) > -1);
        }

        this.setFormTemplateInstancesPropertiesOnField();
    }

    // I have to call this with values
    // With selection list, all selected property names are set at once
    // With a mattable, may have to handle each checkbox click, or get them all on save of properties
    public set FormTemplateInstancesGridJsonConfig(selectedPropertyNames: string[]) {
        this.setformTemplatePropertyIsSelected(selectedPropertyNames);

        this.formTemplateProperties.selectedPropertyNames = selectedPropertyNames;
    }

    private setformTemplatePropertyIsSelected(selectedPropertyNames: string[]) {
        for (let prop of this.formTemplateProperties.availableProperties) {
            prop.isSelected = selectedPropertyNames.indexOf(prop.propertyName) > -1;
        }
    }

    private setFormTemplateInstancesPropertiesOnField(): void {
        // Make this type safe
        let obj = {
            formTemplateProperties: this.formTemplateProperties.availableProperties
        };
        this.field.formTemplateInstancesGridJsonConfig = JSON.stringify(obj);

        let config: FormTemplateInstancesConfig = new FormTemplateInstancesConfig(this.formTemplateProperties.availableProperties);
        let configJson: string = config.toJsonString();

        if (this.field.formTemplateInstancesGridJsonConfig != configJson)
            throw 'FormFieldPropertiesComponent.setPropertiesOnField():  the configuration strings do not match';

        this.field.formTemplateInstancesGridJsonConfig = configJson;
    }

    public getAllowableNumberOfDecimalPoints(includeZero: boolean = false): number[] {
        return includeZero ? [0, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5, 6];
    }

    public get GridColumnBehaviors(): GridColumnBehavior[] {
        return this.gridColumnBehaviors;
    }
    // End methods called by my HTML code to display field proeprties.

    // Handle control events.
    public loadOptionsFromCsvFileClicked(): void {
        this.selectOptionsFile.nativeElement.click();

        return;
    }

    public get DisableMDSbutton(): boolean {
        return !(this.currentUserService.user.isSystemAdmin);
    }

    public loadOptionsFromMDSClicked(): void {
        if (! this.checkSavedFieldBeforeConstraintLoad()) {
            return;
        }

        let data: ConstraintFromMdsDialogModel = new ConstraintFromMdsDialogModel();
        data.fieldId = this.field.id;
        if (this.field.constraintName) {
            data.name = this.field.constraintName;
        }
        else if (this.field.cascadingDropdownConstraintName) {
            data.name = this.field.cascadingDropdownConstraintName;
        }
        else {
            data.name = 'new';
        }

        let dialogRef = this.dialog.open(ConstraintFromMdsDialog, {
            width: "600px",
            data: data
        });

        dialogRef.afterClosed().subscribe(retVal => {
            if (retVal) {
                let cdcl: FormFieldCascadingDropdownConstraintLiaison = new FormFieldCascadingDropdownConstraintLiaison();
                cdcl.setConstraintId(this.field, 133);
                this.optionsLoadedFromMDSFlag = true;
            }
        });

        return;
    }

    public checkSavedFieldBeforeConstraintLoad(): boolean {
        // Options can only be downloaded for a saved field.

        let displaySaveFormMessage: boolean = false;
        if ((this.field.id == null) || (this.field.id <= 0)) {
            displaySaveFormMessage = true;
        } else if (this.optionsLoadedFromMDSFlag == true) {
            displaySaveFormMessage = true;
        } 

        if (displaySaveFormMessage) {
            let message: string = 'Please save the form before downloading options.';
            let dialogData: AlertDialogModel = new AlertDialogModel('Cannot yet download options', message);
            this.dialog.open(AlertDialogComponent, {
                maxWidth: "600px",
                data: dialogData
            });
            return false
        }

        return true;
    }


    public loadTemplateFileClicked(): void {
        this.selectTemplateFile.nativeElement.click();
        return;
    }

    public presetFilterClicked(): void {
        let dialogRef = this.dialog.open(GridPresetFiltersDialog, {
            width: "800px",
            data: new GridPresetFiltersDialogModel(
                this.field.childFormFields
            )
        });

        dialogRef.afterClosed().subscribe(retVal => {
            if (retVal) {
                let filterJson = JSON.parse(this.field.formTemplateInstancesGridJsonConfig);
                if (!filterJson) filterJson = {} as JSON;
                filterJson["presetGridFilters"] = [retVal];
                this.field.formTemplateInstancesGridJsonConfig = JSON.stringify(filterJson);
            }
        });
    }

    public clearCascadingDropdownConfigClicked(): void {
        this.field.jsonConfig = null;
        this.field.csvOptionsData = null;
        this.field.cascadingDropdownConstraintId = null;
        this.field.cascadingDropdownConstraintName = null;
        this.field.cascadingDropdownConstraintValue = null;
        FormFieldPropertiesComponent.clearFileSelection(this.selectOptionsFile);
        FormFieldPropertiesComponent.clearFileSelection(this.selectTemplateFile);
        this.constraintProperties.cascadingDropdownProperties.ConstraintNameProposed = false;

        let hshEventData = {
            propertyName: FormFieldPropertyEnum.SELECT_OPTIONS_FILE,
            formField: this.field
        }
        this.propertyChanged.emit(hshEventData);
    }

    public onTemplateFileChanged(eventData: any): void {
        let templateFileNativeElement = this.selectTemplateFile.nativeElement;
        let templateFile = templateFileNativeElement.files[0];

        this.excelTemplateService.getGridExcelTemplateBase64Representation(templateFile).then(asyncJob => {
            let jobMonitor: MonitorExcelImportToGridHelper = new MonitorExcelImportToGridHelper(this.asyncJobService);
            jobMonitor.startMonitoringJob(asyncJob, this.excelTemplateUploadJobUpdateArrowFunction);
        });
    }

    public onFixedFirstColumnFileChanged(eventData: any): void {
        if (this.onFileChanged(eventData, this.fixedFirstColumnFile.nativeElement, FormFieldPropertyEnum.FIXED_FIRST_COLUMN_FILE)) {
        }
    }

    public onSelectOptionsFileChanged(eventData: any): void {
        if (this.onFileChanged(eventData, this.selectOptionsFile.nativeElement, FormFieldPropertyEnum.SELECT_OPTIONS_FILE)) {
            this.optionsLoadedFromCsvFileFlag = true;
        }
    }

    private onFileChanged(eventData: any, fileUploadElement: any, propertyName: string): boolean {
        if (fileUploadElement.files?.length > 0) {
            let file = fileUploadElement.files[0];
            let filename: string = file.name;

            const reader: FileReader = new FileReader();
            reader.onloadend = (e: any) => {
                let base64FileReadResult: string = e.target.result;
                this.field.csvOptionsData = CsvOptionsFileLoader.loadCsvOptionsFile(base64FileReadResult, propertyName);

                if (this.field.csvOptionsData?.error === null) {
                    // Build the cascading dropdown's configuration.
                    // Handle the file according to the property we are processing
                    if (propertyName == FormFieldPropertyEnum.SELECT_OPTIONS_FILE) {
                        // Propose a constraint name.
                        FormFieldPropertiesComponent.proposeConstraintNameIfApplicable(this.field, this.ProposedConstraintName, this.constraintProperties.cascadingDropdownProperties, this.cascadingDropdownConstraintNameInput, true);
                    } else if (propertyName == FormFieldPropertyEnum.FIXED_FIRST_COLUMN_FILE) {
                        // Set the base 64 form field property.
                        let fixedValues: FixedFirstColumnValues = FixedFirstColumnValues.createFromHeaderValuePairs(this.field.csvOptionsData.headers[0], this.field.csvOptionsData.linesOfHeaderValuePairs);
                        this.field.fixedFirstColumnJson = JSON.stringify(fixedValues);
                    }

                    // Notify parent
                    let hshEventData = {
                        propertyName: propertyName,
                        formField: this.field
                    }
                    this.propertyChanged.emit(hshEventData);
                } else {
                    let msg = 'ERROR: ' + this.field.csvOptionsData?.error;
                    console.log(msg);
                    alert(msg);
                    this.field.csvOptionsData = null;
                    // Need to handle the error here.
                }
            };
            reader.readAsDataURL(file);
            return true;
        }
    }

    public loadFixedFirstColumnFromCsvFileClicked(): void {
        this.fixedFirstColumnFile.nativeElement.click();
    }

    public clearFixedFirstColumn(): void {
        // Clear the corresponding files property.
        FormFieldPropertiesComponent.clearFileSelection(this.fixedFirstColumnFile);

        // Clear the fixed column-related properties.
        this.field.csvOptionsData = null;
        this.field.fixedFirstColumnJson = null;

        // Notify the parent.
        let hshEventData = {
            propertyName: FormFieldPropertyEnum.FIXED_FIRST_COLUMN_FILE,
            formField: this.field
        }
        this.propertyChanged.emit(hshEventData);
    }

    public downloadOptionsAsCsvFileClicked(): void {
        // Options can only be downloaded for a saved field.
        let displaySaveFormMessage: boolean = false;
        if ((this.field.id == null) || (this.field.id <= 0)) {
            displaySaveFormMessage = true;
        } else if (this.optionsLoadedFromCsvFileFlag == true) {
            displaySaveFormMessage = true;
        } else if (this.field.listValuesConstraintId > 0) {
            let url = `${this.formFieldService.getUrlForListValueConstraintCsv(this.field.listValuesConstraintId)}`;
            window.open(url, '_blank');
        } else if (this.field.cascadingDropdownConstraintId > 0) {
            let exportAsCsvOptions: ExportCsvDialogOptions = new ExportCsvDialogOptions();
            FormFieldPropertiesComponent.exportCascadingDropdownConstraintAsCsvFile(this.formFieldService, this.field.cascadingDropdownConstraintId, exportAsCsvOptions, this.field.id, this.field.displayName);
        }
        else {
            console.log('downloadOptionsAsCsvFileClicked received 0 for Constraint ID');
            console.log(this.field);
        }

        if (displaySaveFormMessage) {
            // Cannot yet download the options as a CSV file.
            let message: string = 'Please save the form before downloading options.';
            let dialogData: AlertDialogModel = new AlertDialogModel('Cannot yet download options', message);
            this.dialog.open(AlertDialogComponent, {
                maxWidth: "600px",
                data: dialogData
            });
        }

        return;
    }

    // displays dialog into which options can be pasted
    // as list, which will then be converted to the required
    // pipe delimited list
    public selectOptionsDialog(): void {
        let options = this.field.selectOptions;
        options = options == null ? options : options.replace(/\|/g, '\n');

        let dialogRef = this.dialog.open(SelectOptionsDialog, {
            width: '50%',
            autoFocus: true,
            data: {
                options: options
            }
        });

        dialogRef.afterClosed().subscribe(options => {
            if (options != null) {
                this.field.selectOptions = options.replace(/[\n]/g, "|");
                this.handleModelChange('selectOptions');
            }
        });
    }

    public toggleEditConstraint(siteConstraints: ISiteFormFieldConstraints, primaryPropertyName: string): void {
        if (siteConstraints.editingDisabled) {
            siteConstraints.enableEditing();

            let nameInputField: ElementRef<HTMLInputElement> = this.constraintProperties.mapOfConstraintNameInputFields[primaryPropertyName];
            if (nameInputField != null)
                FormFieldPropertiesComponent.setFocusOnInputControl(nameInputField)
        }
        else
            siteConstraints.disableEditing();
    }

    public handleModelChange(propertyName: string, event: any = null): void {
        //debugger;
        // Certain properties need to be
        // copied to the FormField instance.
        if (propertyName === FormFieldPropertyEnum.NAME) {
            // Determine if there is a uniqueness error.
            if (StringUtil.isEmptyString(this.field.transientToBeAppliedInternalName)) {
                this.fieldNameError = "A field's 'Name' cannot be blank";
            } else if (this.form.nameForFormFieldNotUnique(this.field, this.field.transientToBeAppliedInternalName)) {
                this.fieldNameError = "The field's 'Name' value is not unique";
            } else if (this.field.transientToBeAppliedInternalName.includes(' ')) {
                this.fieldNameError = 'A field internal name cannot contain spaces';
            } else if (this.field.transientToBeAppliedInternalName != this.field.transientToBeAppliedInternalName.toLowerCase()) {
                this.fieldNameError = 'A field internal name cannot contain capital letters';
            } else {
                this.fieldNameError = null;
            }
        }
        else if (propertyName === FormFieldPropertyEnum.GRID_COLUMN_NAME) {
            //VNEXT-1051: grid column name must be a "Javascript identifier"
            //can contain Unicode letters, $, _, and digits (0-9), but may not start with a digit
            if (StringUtil.isEmptyString(this.field.transientToBeAppliedInternalName)) {
                this.fieldNameError = "A field's 'Name' cannot be blank";
            } else if (this.form.nameForFormFieldNotUnique(this.field, this.field.transientToBeAppliedInternalName)) {
                this.fieldNameError = "The field's 'Name' value is not unique";
            } else if (this.field.transientToBeAppliedInternalName.includes(' ')) {
                this.fieldNameError = 'A field internal name cannot contain spaces';
            } else if (this.field.transientToBeAppliedInternalName != this.field.transientToBeAppliedInternalName.toLowerCase()) {
                this.fieldNameError = 'A field internal name cannot contain capital letters';
            } else if (!isNaN(Number(this.field.transientToBeAppliedInternalName.charAt(0)))) {
                this.fieldNameError = 'A grid column internal name cannot start with a digit';
            } else if (!(new RegExp('^[a-zA-Z0-9$_]*$').test(this.field.transientToBeAppliedInternalName))) {
                this.fieldNameError = 'A grid column internal name can contain only letters, numbers, $ and _ symbols';
            }
            else {
                this.fieldNameError = null;
            }
        }
        else if ((propertyName === FormFieldPropertyEnum.SELECT_OPTIONS) ||
            (propertyName === FormFieldPropertyEnum.SELECT_OPTIONS_CONSTRAINT_NAME)) {
            // Skip the following logic unless we are editing an actual form field
            // (rather than a dummy form field associated with the constraints component).
            if (this.hasProperty('selectOptions') || this.hasProperty('selectOptionsProperty')) {
                FormFieldPropertiesComponent.proposeConstraintNameIfApplicable(this.field, this.ProposedConstraintName, this.constraintProperties.selectOptionsProperties, this.selectOptionsConstraintNameInput);

                if ((this.field.selectOptions != null) &&
                    (this.field.selectOptions.trim() != '') &&
                    (this.field.selectOptionsConstraintName != null) &&
                    (this.field.selectOptionsConstraintName.trim() != '')) {
                    this.field.selectOptionsConstraintValue = this.field.selectOptions;

                    this.constraintProperties.selectOptionsProperties.setCurrentFieldConstraintUpdated();
                } else if ((this.field.selectOptions == null) || (this.field.selectOptions.trim() == '')) {
                    // Note:  not sure this branch is correct.
                    this.field.selectOptionsConstraintValue = null;
                }
            }
        } else if ((propertyName === FormFieldPropertyEnum.REGEX) || (propertyName === FormFieldPropertyEnum.REGEX_CONSTRAINT_NAME)) {
            // Skip the following logic unless we are editing an actual form field
            // (rather than a dummy form field associated with the constraints component).
            if (this.hasProperty('regex') || this.hasProperty('regexProperty')) {
                FormFieldPropertiesComponent.proposeConstraintNameIfApplicable(this.field, this.ProposedConstraintName, this.constraintProperties.regularExpressionProperties, this.regularExpressionConstraintNameInput);

                if ((this.field.regex != null) &&
                    (this.field.regex.trim() != '') &&
                    (this.field.regularExpressionConstraintName != null) &&
                    (this.field.regularExpressionConstraintName.trim() != '')) {
                    this.field.regularExpressionConstraintValue = this.field.regex;

                    this.constraintProperties.regularExpressionProperties.setCurrentFieldConstraintUpdated();
                } else if ((this.field.regex == null) || (this.field.regex.trim() == '')) {
                    // Note:  not sure this branch is correct.
                    this.field.regularExpressionConstraintValue = null;
                }
            }
        } else if ((propertyName == FormFieldPropertyEnum.CASCADING_DROPDOWN_CONSTRAINT_NAME) ||
            (propertyName == FormFieldPropertyEnum.SELECTED_CASCADING_DROPDOWN_CONSTRAINT)) {
            // Skip the following logic unless we are editing an actual form field
            // (rather than a dummy form field associated with the constraints component).
            if (this.hasProperty('selectOptionsFile')) {

                if ((this.field.cascadingDropdownConstraintName != null) &&
                    (this.field.cascadingDropdownConstraintName.trim() != '') &&
                    (this.field.jsonConfig != null) && (this.field.jsonConfig.trim() != '')) {
                    this.constraintProperties.cascadingDropdownProperties.setCurrentFieldConstraintUpdated();
                }
            }
        } else if ((propertyName == FormFieldPropertyEnum.NUMERIC_RANGE_CONSTRAINT_NAME) ||
            (propertyName == FormFieldPropertyEnum.SELECTED_NUMERIC_RANGE_CONSTRAINT) ||
            (propertyName == FormFieldPropertyEnum.MIN_VALUE) ||
            (propertyName == FormFieldPropertyEnum.MAX_VALUE)) {
            // Skip the following logic unless we are editing an actual form field
            // (rather than a dummy form field associated with the constraints component).
            if (this.hasProperty('minValue') || this.hasProperty('minValueProperty')) {
                FormFieldPropertiesComponent.proposeConstraintNameIfApplicable(this.field, this.ProposedConstraintName, this.constraintProperties.numericRangeProperties, this.numericRangeConstraintNameInput);

                if (!this.NumericRangeFieldsEmpty) {
                    this.constraintProperties.numericRangeProperties.setCurrentFieldConstraintUpdated();

                    if (propertyName == FormFieldPropertyEnum.MIN_VALUE)
                        this.field.numericRangeConstraintMinValue = this.field.minValue;
                    else if (propertyName == FormFieldPropertyEnum.MAX_VALUE)
                        this.field.numericRangeConstraintMaxValue = this.field.maxValue;
                }
            }
        } else if ((propertyName == FormFieldPropertyEnum.SELECTED_DATE_RANGE_CONSTRAINT) ||
            (propertyName == FormFieldPropertyEnum.DATE_RANGE_CONSTRAINT_NAME) ||
            (propertyName == FormFieldPropertyEnum.MIN_DATE) ||
            (propertyName == FormFieldPropertyEnum.MAX_DATE)) {
            // Skip the following logic unless we are editing an actual form field
            // (rather than a dummy form field associated with the constraints component).
            if (this.hasProperty('minDate') || this.hasProperty('minDateProperty')) {
                FormFieldPropertiesComponent.proposeConstraintNameIfApplicable(this.field, this.ProposedConstraintName, this.constraintProperties.dateRangeProperties, this.dateRangeConstraintNameInput);

                if ((propertyName == FormFieldPropertyEnum.MIN_DATE) && (event != null)) {
                    this.field.minDate = event.value;
                } else if ((propertyName == FormFieldPropertyEnum.MAX_DATE) && (event != null)) {
                    this.field.maxDate = event.value;
                }

                if (!this.DateRangeFieldsEmpty) {
                    this.constraintProperties.dateRangeProperties.setCurrentFieldConstraintUpdated();

                    if (propertyName == FormFieldPropertyEnum.SELECTED_DATE_RANGE_CONSTRAINT) {
                        this.minDateValue.setValue(this.field.minDate);
                        this.maxDateValue.setValue(this.field.maxDate);
                    } else if (propertyName == FormFieldPropertyEnum.MIN_DATE)
                        this.field.dateRangeConstraintMinDate = this.field.minDate;
                    else if (propertyName == FormFieldPropertyEnum.MAX_DATE)
                        this.field.dateRangeConstraintMaxDate = this.field.maxDate;
                }
            }
        } else if (propertyName == FormFieldPropertyEnum.SHOW_CONTACTS_PROPERTIES) {
            this.field.contactFieldNames = (this.contactsProperties != null ? this.contactsProperties.DelimitedCheckedFieldNames : '');
        }
        //VNEXT-519: KLW - Refinements to the Type ahead functionality
        else if (propertyName == FormFieldPropertyEnum.AUTOCOMPLETE) {
            if (this.field.autocomplete) {
                this.field.autocomplete_StartsWith = true;
                this.field.autocomplete_Contains = true;
            }
        } else if (propertyName == FormFieldPropertyEnum.FORM_TEMPLATE) {
            this.field.formTemplateInstancesGridJsonConfig = null;
            this.fetchFormTemplateIfSelected();
        } else if (propertyName == FormFieldPropertyEnum.FORM_TEMPLATE_PROPERTIES) {
        }
        else if (propertyName == FormFieldPropertyEnum.ENABLE_SIMPLE_FORM_GRID_DISPLAY) {
            if (event == false) {
                this.field.formTemplateInstancesGridJsonConfig = null;
                this.fetchFormTemplateIfSelected();

                //***Todo: Reselect items that were already checked*/
            }
        }

        // Note: notify my parent that a property has changed.
        let hshEventData = {
            propertyName: propertyName,
            formField: this.field,
            cancelEdit: propertyName == FormFieldPropertyEnum.CANCEL_ALL
        }
        this.propertyChanged.emit(hshEventData);

        return;
    }

    public handleSettingsFieldBlur(eventData: any, primaryPropertyName: string): void {
        let constraintNameInputField: ElementRef<HTMLInputElement> = this.constraintProperties.mapOfConstraintNameInputFields[primaryPropertyName];
        if (constraintNameInputField != null) {
            setTimeout(() => {
                constraintNameInputField.nativeElement.focus();
                constraintNameInputField.nativeElement.select();
            }, 0);
        }
    }

    // For HtmlText form field properties. Opens a dialog with a large editor
    // and upon closing, writes the changes to the field's htmlText property
    public openHtmlTextDialog(): void {
        let html = this.field.htmlText;

        let dialogRef = this.dialog.open(HtmlEditorDialog, {
            width: '85%',
            height: '75%',
            autoFocus: false,
            data: { html: html }
        });

        dialogRef.afterClosed().subscribe(html => {
            if (html != null) {
                this.field.htmlText = html;
            }
        });
    }

    // Handle save and cancel clicks.
    public saveClicked(): void {

        // Was a new name assigned.
        if (this.field.transientToBeAppliedInternalName != null) {
            this.field.name = this.field.transientToBeAppliedInternalName;
            this.field.transientToBeAppliedInternalName = null;
        }

        // If the internal field name changed, we need to update the layout.
        if (this.originalFieldName != this.field.name) {
            this.form.formLayout?.fieldNameUpdated(this.originalFieldName, this.field.id, this.field.name);
        }

        // Handle properties related to fields that have select options
        // or a regular expression or a cascading dropdown configuration.
        if (this.constraintProperties.selectOptionsProperties && (this.constraintProperties.selectOptionsProperties.getCurrentFieldConstraintUpdated() == true)) {
            this.constraintProperties.selectOptionsProperties.updateOtherFieldsUsingConstraint(this.form, this.field);
        }
        if (this.constraintProperties.regularExpressionProperties && (this.constraintProperties.regularExpressionProperties.getCurrentFieldConstraintUpdated() == true)) {
            this.constraintProperties.regularExpressionProperties.updateOtherFieldsUsingConstraint(this.form, this.field);
        }
        if (this.constraintProperties.cascadingDropdownProperties) {
            if ((this.constraintProperties.cascadingDropdownProperties.getCurrentFieldConstraintUpdated() == true) || (this.field.csvOptionsData != null)) {
                if ((this.field.cascadingDropdownConstraintName?.trim() != '') && (this.field.jsonConfig?.trim() != '') && (this.field.csvOptionsData?.rawCsv.trim() != '')) {

                    if (this.field.csvOptionsData != null) {
                        this.field.cascadingDropdownConstraintValue = this.field.csvOptionsData.rawCsv;
                    }

                    if (this.constraintProperties.cascadingDropdownProperties.getCurrentFieldConstraintUpdated())
                        this.constraintProperties.cascadingDropdownProperties.updateOtherFieldsUsingConstraint(this.form, this.field);
                }
            }
        }
        if ((this.constraintProperties.dateRangeProperties && (!this.DateRangeFieldsEmpty) && this.constraintProperties.dateRangeProperties.getCurrentFieldConstraintUpdated())) {
            this.constraintProperties.dateRangeProperties.updateOtherFieldsUsingConstraint(this.form, this.field);
        }
        if ((this.constraintProperties.numericRangeProperties && (!this.NumericRangeFieldsEmpty) && this.constraintProperties.numericRangeProperties.getCurrentFieldConstraintUpdated())) {
            this.constraintProperties.numericRangeProperties.updateOtherFieldsUsingConstraint(this.form, this.field);
        }

        //***Reset template ids based on multi or single section*/
        if (this.field.enableSimpleFormGridDisplay && this.field.selectedFormTemplateIds?.length > 0) {
            //Todo: Once this code is uncommented, additional changes will be required
            //this.field.formTemplateId = null;
        } else if (!this.field.enableSimpleFormGridDisplay && this.field.formTemplateId > 0) {
            this.field.selectedFormTemplateIds = null;
        }


        // Emit a close event.
        this.onClose.emit(true); // Save changes.
    }

    public cancelClicked(): void {
        //**Note: The form field data will be reverted inside of formBuilderComponent.drawerClosing() */
        if (this.field.transientToBeAppliedInternalName != null)
            this.field.transientToBeAppliedInternalName = null;

        this.field.assignFrom(this.originalField);

        this.handleModelChange(FormFieldPropertyEnum.CANCEL_ALL);

        this.onClose.emit(false); // Do not save changes.
    }

    // Implement private helper methods.
    private static cascadingDropdownConfigRowsAsCsvFile(configRows: CascadingDropdownConfigRow[]): string {
        let csvData: string = '';

        for (let iRow: number = 0; iRow < configRows.length; iRow++) {
            let configRow: CascadingDropdownConfigRow = configRows[iRow];

            if (iRow > 0)
                csvData += "\r\n";

            for (let iValue: number = 0; iValue < configRow.configValues.length; iValue++) {
                if (iValue > 0)
                    csvData += ',';

                csvData += configRow.configValues[iValue];
            }
        }

        return csvData;
    }

    private static fieldNameBlankError(formField: FormField): boolean {
        let error: boolean = ((formField.name == null) || (formField.name.trim() == ''));

        return (error);
    }

    private static setFocusOnInputControl(inputElement: ElementRef): void {
        setTimeout(() => {
            inputElement.nativeElement.focus();
            inputElement.nativeElement.select();
        }, 0);
    }

    private static constraintDupNameError(constraintId: number,
        constraintName: string,
        siteConstraints: ISiteFormFieldConstraints,
        constraintType: string): string {
        let errorMessage: string = null;

        if (siteConstraints != null) {
            if ((constraintId == 0) && (constraintName != null) && (constraintName.trim() != '')) {
                // Note:  here we are using an instance of a dummy constraint type as all we care about is the constraint's name.
                let tempConstraint = <IFormFieldConstraint>new NoValueFormFieldConstraint(0, constraintId, constraintName);
                if (siteConstraints.duplicateNameError(tempConstraint))
                    errorMessage = `A different ${constraintType} constraint already has a name value of '${constraintName}'`;
            }
        }

        return errorMessage;
    }

    private static proposeConstraintNameIfApplicable(field: FormField,
        proposedName: string,
        siteConstraints: ISiteFormFieldConstraints,
        inputField: ElementRef<HTMLInputElement>,
        setInputFieldFocusNow: boolean = false): void {
        let constraintLiaison = siteConstraints.ConstraintLiaison;
        constraintLiaison.proposeConstraintNameIfApplicable(field, proposedName, siteConstraints, inputField, setInputFieldFocusNow);
    }

    private isEmptyString(value: string): boolean {
        return StringUtil.isEmptyString(value);
    }

    private static clearFileSelection(filesField: ElementRef<HTMLInputElement>): void {
        const dt = new DataTransfer();
        filesField.nativeElement.files = dt.files;
    }

    private static exportCascadingDropdownConstraintAsCsvFile(formFieldService: CachedFormFieldService,
        cascadingDropdownConstraintId: number,
        exportAsCsvOptions: ExportCsvDialogOptions,
        fieldId: number,
        fieldDisplayName: string): void {
        // Export the data as a CSV file.
        formFieldService.getCascadingDropdownConstraintConfiguration(cascadingDropdownConstraintId, exportAsCsvOptions.wrapFieldsInDoubleQuotes, exportAsCsvOptions.fieldDelimiter).then(configRows => {
            let csvContent: string = FormFieldPropertiesComponent.cascadingDropdownConfigRowsAsCsvFile(configRows);

            let a = document.createElement("a");
            a.href = window.URL.createObjectURL(new Blob([csvContent], { type: "text/plain" }));
            let downloadFilename: string =
                ((fieldDisplayName != null) && (fieldDisplayName.trim() != '') ? `${fieldDisplayName}_options.csv` : `field_${fieldId}_options.csv`);
            a.download = downloadFilename;
            a.click();
        });
    }
    // Opens the dialog for configuring a Site Home Page Grid/Form Grid
    public openConfigureHomePageColumns(): void {
        let dialogRef = this.dialog.open(ConfigureHomePageGridColumnsDialog, {
            width: '50%',
            data: {
                availableProperties: this.formTemplateProperties.availableProperties,
                selectedFormTemplatePropertiesIndexes: this.selectedFormTemplatePropertiesIndexes,
                formTemplateProperties: this.formTemplateProperties,
                formTemplatePropertiesGridSelectionSettings: this.formTemplatePropertiesGridSelectionSettings,
                selectedFormTemplateProperties: this.SelectedFormTemplateProperties,
                selectedGridFieldId: this.selectedGridFieldId,
                commandColumnPostion: this.commandColumnPostion,
                formTemplateInstancesButtons: this.formTemplateInstancesButtons,
                field: this.field,
                form: this.formTemplateProperties.selectedFormTemplate
            }
        });

        dialogRef.afterClosed().subscribe(data => {
            if (data != null) {
                let templateInstancesConfig: FormTemplateInstancesConfig = <FormTemplateInstancesConfig>data.templateInstancesConfig;
                let actionButtonsConfig: FormTemplateInstancesButtons = <FormTemplateInstancesButtons>data.actionButtonsConfig;
                this.field.formTemplateInstanceActionButtonsConfig = actionButtonsConfig.toFormFieldPropertyValue();

                let configJson: string = templateInstancesConfig.toJsonString();
                this.field.formTemplateInstancesGridJsonConfig = configJson;
            }
        });
    }

    // Manage images.
    public openUploadImageDialogue(): void {
        let dialogInitInfo = null;

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.height = '325px';
        dialogConfig.data = dialogInitInfo;

        const dialogRef = this.dialog.open(UploadIconDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(result => {
        });
    }

    public openListImagesDialogue(): void {
        let dialogInitInfo = null;

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';
        dialogConfig.height = '450px';
        dialogConfig.data = dialogInitInfo;

        const dialogRef = this.dialog.open(ListIconsDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(result => {
        });
    }

    /*
        It's possible that the UI is doing too much here. Do we need to build the Column defs in order to pass them to along to the directive and service
        when the server already has what it needs from the parsing of the same field? Not sure -- would require analysis

        This method is for "Form Template Instance Grids" - aka Home Page Grids 
        It's called from ngOnInit() and handleModelChange() if the FormField being configured has the "formTemplateProperties" property.
        It populates
        - this.formTemplateProperties.availableProperties:      the possible columns that could be configured for a Home Page Grid
        - this.selectedFormTemplatePropertiesIndexes:           the indexes of those available properties that the user has selected (if any)
        - this.selectedGridFieldId                              the id of the Grid field the user has selected (only one can be selected per Home Page Grid)
    */
    private fetchFormTemplateIfSelected(): void {
        this.formTemplateProperties.selectedPropertyNames = [];
        this.formTemplateProperties.availableProperties = [];
        //debugger
        //***Check if this is a simple form grid*/
        if (this.field.enableSimpleFormGridDisplay) {
            this.fetchMultipleFormTemplatesIfSelected();

            return;
        }

        if ((this.field.formTemplateId != null) && (this.field.formTemplateId > 0)) {
            this.formBuilderService.get(this.field.formTemplateId).then(response => {
                this.formTemplateProperties.selectedFormTemplate = response;

                if (this.formTemplateProperties.selectedFormTemplate != null) {
                    // If fields have been selected and/or sorted, the properties will have been saved as JSON in the Field's formTemplateInstancesGridJsonConfig column
                    if ((this.field.formTemplateInstancesGridJsonConfig != null) && (this.field.formTemplateInstancesGridJsonConfig.trim() != '')) {
                        let jsonSavedProperties: FormTemplateInstancesConfig = FormTemplateInstancesConfig.fromJsonString(this.field.formTemplateInstancesGridJsonConfig);

                        let propertyNames: string[] = jsonSavedProperties.FormTemplateProperties.filter(x => { return x.isSelected }).map(x => x.propertyName);
                        let availableFieldNames = jsonSavedProperties.FormTemplateProperties.map(x => x.propertyName);

                        this.formTemplateProperties.availableProperties = [];
                        for (let prop of jsonSavedProperties.FormTemplateProperties) {
                            let propertyInfo: FormTemplatePropertyInfo = prop as FormTemplatePropertyInfo;
                            this.setDefaultFilterOperatorIfNotSet(propertyInfo);

                            this.formTemplateProperties.availableProperties.push(propertyInfo);
                        }

                        let indexes = [];
                        for (let index: number = 0; index < propertyNames.length; index++) {
                            let selectedIndex = availableFieldNames.indexOf(propertyNames[index]);
                            indexes.push(selectedIndex);
                        }
                        this.selectedFormTemplatePropertiesIndexes = indexes;
                        this.selectedGridFieldId = jsonSavedProperties.SelectedGridFieldId;
                        this.commandColumnPostion = jsonSavedProperties.CommandColumnPostion;
                    } else {
                        this.formTemplateProperties.availableProperties = MetadataFieldFactory.getFormTemplateInstancesMetadataProperties();

                        // Ordinary form template fields.
                        if (this.formTemplateProperties.selectedFormTemplate.formFields != null) {
                            for (let index: number = 0; index < this.formTemplateProperties.selectedFormTemplate.formFields.length; index++) {
                                let formField: FormField = this.formTemplateProperties.selectedFormTemplate.formFields[index];

                                let fieldDefClientLogic: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(formField.fieldDefinitionClassName);
                                if (fieldDefClientLogic.includeInFormTemplateInstancesGrid()) {
                                    // If the form-level field has virtual fields, only add those virtual fields, not the concrete field itself
                                    let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(formField.fieldDefinitionClassName);
                                    if (fieldDef?.customLogicHandler?.hasVirtualSiblingFormFieldsFor(formField)) {
                                        let virtualSiblingFields = fieldDef.customLogicHandler.getVirtualSiblingFormFieldsFor(formField);
                                        if (virtualSiblingFields?.length > 0) {
                                            for (let virtualField of virtualSiblingFields) {
                                                virtualField.id = formField.id;
                                                let propertyInfo = new FormTemplatePropertyInfo(virtualField.displayName, FormTemplatePropertyInfo.FORM_LEVEL_FIELD, false, virtualField);
                                                this.formTemplateProperties.availableProperties.push(propertyInfo);
                                            }
                                        }
                                    } else {
                                        let name = formField.displayName != null ? formField.displayName : formField.name;
                                        let isGrid = formField.fieldDefinitionClassName == GridFieldType;
                                        let fieldType = isGrid ? FormTemplatePropertyInfo.GRID_FIELD : FormTemplatePropertyInfo.FORM_LEVEL_FIELD;
                                        let propertyInfo: FormTemplatePropertyInfo = new FormTemplatePropertyInfo(name, fieldType, isGrid, formField, this.fieldDefinitionService).setDefaultFilterOperator(fieldDefClientLogic.getDefaultKendoGridOperatorName());
                                        this.formTemplateProperties.availableProperties.push(propertyInfo);
                                    }
                                }
                            }
                        }
                    }
                }
            });
        } else {
            this.formTemplateProperties.selectedFormTemplate = null;
        }
    }

    private fetchMultipleFormTemplatesIfSelected(): void {
        //debugger;
        if ((this.field.selectedFormTemplateIds != null) && (this.field.selectedFormTemplateIds.length > 0)) {
            // If fields have been selected and/or sorted, the properties will have been saved as JSON in the Field's formTemplateInstancesGridJsonConfig column
            if ((this.field.formTemplateInstancesGridJsonConfig != null) && (this.field.formTemplateInstancesGridJsonConfig.trim() != '')) {
                let jsonSavedProperties: FormTemplateInstancesConfig = FormTemplateInstancesConfig.fromJsonString(this.field.formTemplateInstancesGridJsonConfig);



                let propertyNames: string[] = jsonSavedProperties.FormTemplateProperties.filter(x => { return x.isSelected }).map(x => x.propertyName);
                let availableFieldNames = jsonSavedProperties.FormTemplateProperties.map(x => x.propertyName);

                this.formTemplateProperties.availableProperties = [];
                for (let prop of jsonSavedProperties.FormTemplateProperties) {
                    let propertyInfo: FormTemplatePropertyInfo = prop as FormTemplatePropertyInfo;
                    this.setDefaultFilterOperatorIfNotSet(propertyInfo);

                    this.formTemplateProperties.availableProperties.push(propertyInfo);
                }

                let indexes = [];
                for (let index: number = 0; index < propertyNames.length; index++) {
                    let selectedIndex = availableFieldNames.indexOf(propertyNames[index]);
                    indexes.push(selectedIndex);
                }
                this.selectedFormTemplatePropertiesIndexes = indexes;
                this.selectedGridFieldId = jsonSavedProperties.SelectedGridFieldId;
                this.commandColumnPostion = jsonSavedProperties.CommandColumnPostion;

                //***Add code to compare all available metadata fields with the fields that were saved. If the field doesn't exist, add it*/
                let allAvailableMetadataProps: FormTemplatePropertyInfo[] = MetadataFieldFactory.getFormTemplateInstancesMetadataProperties();
                let currMetadataPropertyIndex: number = 0;

                for (let prop of allAvailableMetadataProps) {
                    let propertyInfo: FormTemplatePropertyInfo = prop as FormTemplatePropertyInfo;
                    this.setDefaultFilterOperatorIfNotSet(propertyInfo);

                    //***Add prop to list if not already there*/
                    let metadataPropertyInList: boolean = this.formTemplateProperties.availableProperties.some(x => x.propertyName == propertyInfo.propertyName);
                    if (!metadataPropertyInList) {
                        //this.formTemplateProperties.availableProperties.push(propertyInfo);

                        //**Adds linkedFormInstance to beginning of the list */
                        this.formTemplateProperties.availableProperties.splice(currMetadataPropertyIndex, 0, propertyInfo);
                    }

                    currMetadataPropertyIndex++;
                }
            } else {
                this.formTemplateProperties.availableProperties = MetadataFieldFactory.getFormTemplateInstancesMetadataProperties();
            }
        } else {
            this.formTemplateProperties.selectedFormTemplate = null;
        }
    }

    private setDefaultFilterOperatorIfNotSet(propertyInfo: FormTemplatePropertyInfo): void {
        if (propertyInfo.defaultFilterOperator == null) {
            if (propertyInfo.type == 'metadata') {
                switch (propertyInfo.propertyName) {
                    case 'LinkedFormInstance':
                    case 'Favorite':
                    case 'Name':
                    case 'FolderName':
                    case 'FolderPath':
                    case 'WorkflowState':
                    case 'CreatedBy':
                    case 'ModifiedBy':
                        propertyInfo.defaultFilterOperator = KendoGridOperatorName.Contains;
                        break;
                    case 'Id':
                    case 'CreatedDate':
                    case 'ModifiedDate':
                        propertyInfo.defaultFilterOperator = KendoGridOperatorName.Equals;
                        break;
                    default:
                        propertyInfo.defaultFilterOperator = KendoGridOperatorName.Equals;
                        break;
                }
            } else if (propertyInfo.type == 'field') {
                let formField: FormField = this.form.formFields.find(ff => ff.id == propertyInfo.fieldId);
                if (formField != null) {
                    let fieldDefClientLogic: IFieldDefinitionLogic = this.fieldDefinitionService.getFieldClientLogicHandler(formField.fieldDefinitionClassName);
                    propertyInfo.defaultFilterOperator = fieldDefClientLogic.getDefaultKendoGridOperatorName();
                }
            }
        }
    }

    private excelTemplateUploadJobUpdateArrowFunction = (asyncJob: AsyncJob): void => {
        if (asyncJob.status == AsyncJob.STATUS_COMPLETE) {
            this.excelTemplateService.getGridExcelTemplateBase64RepresentationResult(asyncJob).then(response => {
                this.field.excelImportExportTemplateBase64Encoded = response.templateBase64Representation;
            });
        }
    }
}
